import { useApolloClient } from '@apollo/client';
import { isObject } from 'lodash/fp';
import { useCallback } from 'react';
import { type FinderApplicationModuleEmailContentSpecsFragment } from '../../../../api/fragments';
import { type UploadFileWithPreviewFormDataFragment } from '../../../../api/fragments/UploadFileWithPreviewFormData';
import {
    DeleteFinderApplicationModuleAssetDocument,
    type DeleteFinderApplicationModuleAssetMutation,
    type DeleteFinderApplicationModuleAssetMutationVariables,
} from '../../../../api/mutations/deleteFinderApplicationModuleAsset';
import {
    UploadFinderApplicationModuleAssetDocument,
    type UploadFinderApplicationModuleAssetMutation,
    type UploadFinderApplicationModuleAssetMutationVariables,
} from '../../../../api/mutations/uploadFinderApplicationModuleAsset';
import { type EmailContentUpdateType, FinderApplicationModuleAsset } from '../../../../api/types';
import { type FinderApplicationEmailFormValues } from './type';

type UploadParams = {
    moduleId: string;
    upload: File | UploadFileWithPreviewFormDataFragment;
    type: FinderApplicationModuleAsset;
    emailContentUpdateType: EmailContentUpdateType;
    dealerId?: string;
};

const useHandleFinderApplicationModuleAssets = () => {
    const apolloClient = useApolloClient();

    const updateOrDelete = useCallback(
        (
            { type, moduleId, upload, emailContentUpdateType, dealerId }: UploadParams,
            initialValue: UploadFileWithPreviewFormDataFragment
        ) => {
            const uploads = [];

            if (upload instanceof File) {
                // upload a new file
                uploads.push(
                    apolloClient.mutate<
                        UploadFinderApplicationModuleAssetMutation,
                        UploadFinderApplicationModuleAssetMutationVariables
                    >({
                        mutation: UploadFinderApplicationModuleAssetDocument,
                        variables: {
                            moduleId,
                            type,
                            upload,
                            emailContentUpdateType,
                            dealerId,
                        },
                    })
                );
            } else if (upload === null && isObject(initialValue)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<
                        DeleteFinderApplicationModuleAssetMutation,
                        DeleteFinderApplicationModuleAssetMutationVariables
                    >({
                        mutation: DeleteFinderApplicationModuleAssetDocument,
                        variables: { moduleId, type, emailContentUpdateType, dealerId },
                    })
                );
            }

            return uploads;
        },
        [apolloClient]
    );

    return useCallback(
        (
            moduleId: string,
            values: FinderApplicationEmailFormValues,
            emailContentUpdateType: EmailContentUpdateType,
            initialValues?: FinderApplicationModuleEmailContentSpecsFragment,
            dealerId?: string
        ) => {
            const { submitOrder, reminderEmail } = values;

            const uploads = [
                ...updateOrDelete(
                    {
                        moduleId,
                        upload: submitOrder.introImage,
                        type: FinderApplicationModuleAsset.SubmitOrderIntro,
                        emailContentUpdateType,
                        dealerId,
                    },
                    initialValues?.submitOrder.introImage
                ),
                ...updateOrDelete(
                    {
                        moduleId,
                        upload: reminderEmail.introImage,
                        type: FinderApplicationModuleAsset.ReminderEmailIntro,
                        emailContentUpdateType,
                        dealerId,
                    },
                    initialValues?.reminderEmail.introImage
                ),
            ];

            if (uploads.length) {
                return Promise.all(uploads);
            }

            return Promise.resolve([]);
        },
        [updateOrDelete]
    );
};

export default useHandleFinderApplicationModuleAssets;
